import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Navbar.css';
import logo from '../../assets/logo.png';

const navLabels = {
  ne: [
    { to: '/',           label: 'गृहपृष्ठ',      end: true },
    { to: '/events',     label: 'कार्यक्रम' },
    { to: '/news',       label: 'समाचार' },
    { to: '/downloads',  label: 'डाउनलोड' },
    { to: '/about',      label: 'हाम्रो बारेमा' },
  ],
  en: [
    { to: '/',           label: 'Home',      end: true },
    { to: '/events',     label: 'Events' },
    { to: '/news',       label: 'News' },
    { to: '/downloads',  label: 'Downloads' },
    { to: '/about',      label: 'About Us' },
  ],
};

const togethernessLabels = {
  ne: {
    trigger: 'सहकार्य',
    items: [
      { to: '/expertslist',   label: 'विज्ञहरू' },
      { to: '/volunteerslist', label: 'स्वयंसेवकहरू' },
    ],
  },
  en: {
    trigger: 'Togetherness',
    items: [
      { to: '/expertslist',   label: 'Experts' },
      { to: '/volunteerslist', label: 'Volunteers' },
    ],
  },
};

const Navbar = () => {
  const { language, toggleLanguage } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled]  = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const t = navLabels[language] || navLabels.ne;
  const tg = togethernessLabels[language] || togethernessLabels.ne;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setMenuOpen(false);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setMobileDropdownOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} role="navigation" aria-label="Main navigation">
      <div className="navbar__container">

        {/* Logo */}
        <Link to="/" className="navbar__logo" onClick={closeMenu} aria-label="Bholi Ko Itahari Home">
          <div className="navbar_logo" style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '150px', height: '50px' }}>
            <img src={logo} alt="Bholi Ko Itahari Logo" className="navbar__logo-image" />
          </div>
        </Link>

        {/* Desktop nav links */}
        <ul className="navbar__links" role="menubar">
          {t.map(({ to, label, end }) => (
            <li key={to} role="none">
              <NavLink
                to={to}
                end={end}
                role="menuitem"
                className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
              >
                {label}
              </NavLink>
            </li>
          ))}

          {/* Togetherness dropdown */}
          <li role="none" className="navbar__dropdown" ref={dropdownRef}>
            <button
              type="button"
              role="menuitem"
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              className={`navbar__link navbar__dropdown-trigger ${dropdownOpen ? 'navbar__link--active' : ''}`}
              onClick={() => setDropdownOpen(o => !o)}
            >
              {tg.trigger} <span className="navbar__dropdown-caret">▾</span>
            </button>

            {dropdownOpen && (
              <ul className="navbar__dropdown-menu" role="menu">
                {tg.items.map(({ to, label }) => (
                  <li key={to} role="none">
                    <NavLink
                      to={to}
                      role="menuitem"
                      className={({ isActive }) => `navbar__dropdown-item ${isActive ? 'navbar__dropdown-item--active' : ''}`}
                      onClick={() => setDropdownOpen(false)}
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>

        {/* Auth buttons */}
        <div className="navbar__auth">
          <button className="language-toggle" type="button" onClick={toggleLanguage}>
            {language === 'ne' ? 'EN' : 'नेपाली'}
          </button>
          {isAuthenticated && (
            <div className="navbar__user">
              <div className="navbar__avatar" title={user?.name}>
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="navbar__user-info">
                <span className="navbar__user-name">{user?.name}</span>
                <span className="navbar__user-role">{user?.role}</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
                {language === 'ne' ? 'लगआउट' : 'Logout'}
              </button>
            </div>
          )}
        </div>

        {/* Hamburger */}
        <button
          className={`navbar__hamburger ${menuOpen ? 'navbar__hamburger--open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="navbar__mobile" role="dialog" aria-label="Mobile navigation">
          <ul>
            {t.map(({ to, label, end }) => (
              <li key={to}>
                <NavLink to={to} end={end} onClick={closeMenu}
                  className={({ isActive }) => isActive ? 'active' : ''}>{label}</NavLink>
              </li>
            ))}

            {/* Togetherness mobile submenu */}
            <li className="navbar__mobile-dropdown">
              <button
                type="button"
                className="navbar__mobile-dropdown-trigger"
                aria-expanded={mobileDropdownOpen}
                onClick={() => setMobileDropdownOpen(o => !o)}
              >
                {tg.trigger} <span className={`navbar__dropdown-caret ${mobileDropdownOpen ? 'navbar__dropdown-caret--open' : ''}`}>▾</span>
              </button>
              {mobileDropdownOpen && (
                <ul className="navbar__mobile-submenu">
                  {tg.items.map(({ to, label }) => (
                    <li key={to}>
                      <NavLink to={to} onClick={closeMenu}
                        className={({ isActive }) => isActive ? 'active' : ''}>{label}</NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>
          <div className="navbar__mobile-auth">
            <button className="language-toggle" type="button" onClick={toggleLanguage}>
              {language === 'ne' ? 'EN' : 'नेपाली'}
            </button>
            {isAuthenticated && (
              <button className="btn btn-ghost" onClick={handleLogout}>
                {language === 'ne' ? 'लगआउट' : 'Logout'}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;